import { Link } from 'react-router-dom'
import { useMangaList, useHistory, useMeta } from '../api/useManga'
import { useAuthStore } from '../store/authStore'
import { mediaUrl } from '../utils/media'
import MangaCard from '../components/manga/MangaCard'
import { MangaCardSkeleton } from '../components/ui/Skeleton'

export default function Home() {
  const { user, isAuth } = useAuthStore()
  const showAdult = isAuth ? (user?.show_adult ?? false) : localStorage.getItem('manga_show_adult') === 'true'
  const adultParam = showAdult ? { show_adult: true } : {}

  const { data: popular, isLoading: loadingPop } = useMangaList({ sort: 'rating', limit: 12, ...adultParam })
  const { data: newest, isLoading: loadingNew } = useMangaList({ sort: 'date', limit: 10, ...adultParam })
  const { data: banner } = useMangaList({ sort: 'rating', limit: 1, ...adultParam })
  const { data: history = [] } = useHistory()
  const { data: meta } = useMeta()

  const bannerManga = banner?.items?.[0] || banner?.[0]
  const cover = mediaUrl(bannerManga?.cover_url)

  return (
    <div className="overflow-x-hidden">
      <title>{import.meta.env.VITE_SITE_NAME} — читать мангу онлайн</title>
      <meta name="description" content="Читайте мангу онлайн на русском языке. Большой каталог с фильтрами по жанрам, рейтингу и статусу." />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={import.meta.env.VITE_SITE_NAME} />
      <meta property="og:title" content={`${import.meta.env.VITE_SITE_NAME} — читать мангу онлайн`} />
      <meta property="og:description" content="Читайте мангу онлайн на русском языке. Большой каталог с фильтрами по жанрам, рейтингу и статусу." />
      <link rel="canonical" href={`${window.location.origin}/`} />
      {bannerManga && (
        <section className="relative overflow-hidden" style={{ minHeight: 380 }}>
          {cover && (
            <>
              <div className="absolute inset-0" style={{
                backgroundImage: `url(${cover})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center 20%',
                filter: 'blur(28px) brightness(0.22) saturate(1.4)',
                transform: 'scale(1.12)',
              }} />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 50%, var(--bg) 100%)' }} />
            </>
          )}

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 flex items-center gap-8 sm:gap-10">
            {cover && (
              <div className="hidden sm:block shrink-0" style={{ width: 160 }}>
                <img src={cover} alt={bannerManga.title} className="w-full rounded-2xl"
                  style={{ aspectRatio: '2/3', objectFit: 'cover', boxShadow: '0 16px 48px rgba(0,0,0,0.7)' }} />
              </div>
            )}

            <div className="flex flex-col gap-4 min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent">★ В тренде</p>
              <h1 className="text-3xl sm:text-4xl leading-tight"
                style={{ color: cover ? '#ffffff' : 'var(--text)', maxWidth: 560 }}>
                {bannerManga.title}
              </h1>
              {bannerManga.description && (
                <p className="text-sm leading-relaxed line-clamp-3"
                  style={{ color: cover ? 'rgba(255,255,255,0.72)' : 'var(--muted)', maxWidth: 520 }}>
                  {bannerManga.description}
                </p>
              )}
              <div className="flex items-center gap-3 flex-wrap">
                <Link to={`/manga/${bannerManga.slug || bannerManga.id}`}
                  className="px-6 py-2.5 rounded-xl font-bold text-sm transition-all hover:opacity-90 active:scale-[0.97] bg-accent text-on-accent">
                  Читать сейчас
                </Link>
                <Link to="/catalog"
                  className="px-6 py-2.5 rounded-xl font-semibold text-sm transition-all"
                  style={{
                    border: `1px solid ${cover ? 'rgba(255,255,255,0.2)' : 'var(--border)'}`,
                    color: cover ? 'rgba(255,255,255,0.8)' : 'var(--text)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--color-accent)')}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = cover ? 'rgba(255,255,255,0.2)' : 'var(--border)')}>
                  Весь каталог
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {isAuth && history.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10">
          <p className="text-sm font-semibold text-fg mb-3">Продолжить чтение</p>
          <div className="flex gap-2 flex-wrap">
            {history.slice(0, 5).map((h) => (
              <Link
                key={h.id}
                to={`/manga/${h.manga_slug}/chapter/${h.chapter_number}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-line bg-surface hover:border-accent hover:text-accent transition-colors"
              >
                <span className="relative group/tip font-medium">
                  <span className="block max-w-[180px] truncate">{h.manga_title}</span>
                  <span className="pointer-events-none absolute bottom-full left-0 mb-1.5 px-3 py-1.5 rounded-md text-sm bg-surface2 border border-line text-fg whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity duration-75 z-20">
                    {h.manga_title}
                  </span>
                </span>
                {h.chapter_number != null && (
                  <span className="text-muted shrink-0">· гл.{h.chapter_number}</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col gap-14">
        <section>
          <SectionTitle title="Новинки" linkTo="/catalog?sort=date" linkLabel="Смотреть все" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {loadingNew
              ? Array.from({ length: 10 }).map((_, i) => <MangaCardSkeleton key={i} className={i >= 6 ? 'hidden sm:block' : ''} />)
              : (newest?.items || newest || []).map((m, i) => (
                  <div key={m.id || m.slug} className={i >= 6 ? 'hidden sm:block' : ''}>
                    <MangaCard manga={m} meta={meta} />
                  </div>
                ))}
          </div>
        </section>

        <section>
          <SectionTitle title="Популярное" linkTo="/catalog?sort=rating" linkLabel="Весь каталог" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {loadingPop
              ? Array.from({ length: 12 }).map((_, i) => <MangaCardSkeleton key={i} className={i >= 6 ? 'hidden sm:block' : ''} />)
              : (popular?.items || popular || []).map((m, i) => (
                  <div key={m.id || m.slug} className={i >= 6 ? 'hidden sm:block' : ''}>
                    <MangaCard manga={m} meta={meta} />
                  </div>
                ))}
          </div>
        </section>
      </div>
    </div>
  )
}

function SectionTitle({ title, linkTo, linkLabel }) {
  return (
    <div className="flex items-center justify-between mb-5">
      <h2 className="text-xl text-fg">{title}</h2>
      {linkTo && (
        <Link to={linkTo} className="text-sm font-medium text-muted hover:text-accent transition-colors">
          {linkLabel} →
        </Link>
      )}
    </div>
  )
}
